import { Component, computed, inject, signal, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'

import { TranslatePipe } from '@ngx-translate/core'
import { DialogModule } from 'primeng/dialog'
import { InputNumberModule } from 'primeng/inputnumber'
import { TagModule } from 'primeng/tag'
import { finalize } from 'rxjs'

import { ButtonComponent } from 'app/_shared/components/button/button.component'
import { CartItemComponent } from 'app/_shared/components/cart-item/cart-item.component'
import { EmptyPageComponent } from 'app/_shared/components/empty-page/empty-page.component'
import { UsdPipe } from 'app/_shared/pipes/currency.pipe'
import { PricePipe } from 'app/_shared/pipes/price.pipe'
import { CheckoutService } from 'app/_shared/services/checkout.service'
import { AppConfigService } from 'app/app-config.service'
import { CartStore } from 'app/store/cart.store'
import { GiftCardStore } from 'app/store/gift-card.store'

@Component({
  selector: 'app-cart',
  imports: [
    FormsModule,
    TranslatePipe,
    PricePipe,
    UsdPipe,
    EmptyPageComponent,
    ButtonComponent,
    TagModule,
    InputNumberModule,
    DialogModule,
    CartItemComponent
  ],
  templateUrl: './cart.component.html',
  styleUrl: './cart.component.scss'
})
export class CartComponent implements OnInit {
  cartStore = inject(CartStore)
  checkoutService = inject(CheckoutService)
  private router = inject(Router)
  private giftCardStore = inject(GiftCardStore)

  inactiveVariantIds = signal<number[]>([])
  isLoadingVariantCheck = false
  appConfigService = inject(AppConfigService)
  storefrontId: number
  protected disablePrices: boolean
  protected get canCheckout(): boolean {
    return this.appConfigService.canCheckout
  }

  displayRemoveDialog: boolean = false
  selectedItemIndex: number | null = null

  items = computed(() => this.cartStore.items())
  hidePrices = computed(() => this.cartStore.hideProductPrices())
  isGifting = computed(() => !!this.cartStore.giftingToken())
  totalPrice = computed(() =>
    this.cartStore
      .items()
      .reduce((accumulator, current) => accumulator + current.price * +current.quantity, 0)
  )

  giftCardBalance = computed(() => Number(this.giftCardStore.balance() ?? 0))

  totalPriceWithGiftCard = computed(() => {
    const total = this.totalPrice()
    const gc = this.giftCardBalance()
    return Math.max(total - gc, 0)
  })

  // Group items by warehouse
  warehouseGroups = computed(() => {
    const items = this.items()
    const warehouseMap = new Map<
      string,
      {
        warehouseId: number | null
        warehouseName: string
        warehouseDisplayName?: string
        items: ((typeof items)[0] & { originalIndex: number })[]
        subtotal: number
      }
    >()

    items.forEach((item, index) => {
      let groupKey: string
      let wId: number | null
      let wName: string

      if (item.warehouse) {
        groupKey = `warehouse_${item.warehouse.id}`
        wId = item.warehouse.id
        wName = item.warehouse.name
      } else {
        groupKey = 'gift_cards'
        wId = null
        wName = 'Gift Cards'
      }

      if (!warehouseMap.has(groupKey)) {
        warehouseMap.set(groupKey, {
          warehouseId: wId,
          warehouseName: wName,
          items: [],
          subtotal: 0
        })
      }

      const group = warehouseMap.get(groupKey)!
      group.items.push({ ...item, originalIndex: index })
      group.subtotal += item.price * item.quantity
    })

    // Convert map to array, sort by warehouseId (gift cards will be last since they have null id)
    const groups = Array.from(warehouseMap.values()).sort((a, b) => {
      if (a.warehouseId === null) return 1
      if (b.warehouseId === null) return -1
      return a.warehouseId - b.warehouseId
    })

    // Assign warehouseDisplayName in sorted order (Parcel 1, Parcel 2, ...)
    groups.forEach((group, index) => {
      group.warehouseDisplayName = `Parcel ${index + 1}`
    })

    return groups
  })

  warehouseCount = computed(() => this.warehouseGroups().length)

  removeItem(index: number) {
    this.selectedItemIndex = index
    this.displayRemoveDialog = true
  }

  confirmRemove() {
    if (this.selectedItemIndex !== null) {
      this.cartStore.removeCartItem(this.selectedItemIndex)
      this.selectedItemIndex = null
    }
    this.displayRemoveDialog = false
  }

  cancelRemove() {
    this.selectedItemIndex = null
    this.displayRemoveDialog = false
  }

  onQuantityChange(index: number, newValue: number) {
    this.cartStore.updateQuantity(index, newValue)
  }

  hasInactiveItems = computed(() =>
    this.items().some(item => this.inactiveVariantIds().includes(item.product_variant_id))
  )

  ngOnInit() {
    this.storefrontId = this.appConfigService.getAppConfig?.id
    this.disablePrices = !!this.appConfigService.getAppConfig.disabled_prices

    this.checkProductVariants()
  }

  private checkProductVariants() {
    const currentItems = this.items()
    if (!currentItems.length) {
      return
    }

    const productVariantIds = [...new Set(currentItems.map(item => item.product_variant_id))]

    this.isLoadingVariantCheck = true
    this.checkoutService
      .checkProductVariants(this.storefrontId, productVariantIds)
      .pipe(
        finalize(() => {
          this.isLoadingVariantCheck = false
        })
      )
      .subscribe({
        next: res => {
          const { inactive_product_variants } = res.data
          this.inactiveVariantIds.set(inactive_product_variants || [])
        },
        error: err => {
          console.error('Error checking product variants:', err.error?.message)
          this.inactiveVariantIds.set([])
        }
      })
  }

  goToCheckout() {
    if (!this.hasInactiveItems() && this.canCheckout) {
      this.router.navigate(['/checkout'])
    }
  }
}
